import { getAuth } from "firebase/auth";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";
import {
  Button,
  Group,
  NumberInput,
  Select,
  Textarea,
  TextInput,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { fetchUser, storeImage, updateCurrentUser } from "./ProfileUtil";
import UserAvatar from "../components/UserAvatar";
import { countries } from "../utils/countries";
import { UserType } from "../utils/types";
import * as lists from "../utils/listValues";

export default function MyProfile() {
  const auth = getAuth();
  const navigate = useNavigate();
  const intl = useIntl();

  const [user, setUser] = useState<UserType | null>(null);
  const [availTooltipOpened, setAvailTooltipOpened] = useState(false);

  const [formData, setFormData] = useState({
    name: auth.currentUser?.displayName ?? "",
    email: auth.currentUser?.email ?? "",
    photoURL: user?.photoURL ?? "",
    country: user?.country ?? "",
    city: user?.city ?? "",
    reason: user?.reason ?? "",
    profArea: user?.profArea ?? "",
    availability: user?.availability ?? "",
    description: user?.description ?? "",
    salary: user?.salary ?? 0,
  });

  const {
    name,
    email,
    photoURL,
    country,
    city,
    reason,
    profArea,
    availability,
    description,
    salary,
  } = formData;

  useEffect(() => {
    if (auth.currentUser?.uid) {
      fetchUser(auth.currentUser?.uid, setUser);
    }
  }, [auth.currentUser?.uid]);

  useEffect(() => {
    if (user)
      setFormData((prevState) => ({
        ...prevState,
        photoURL: user.photoURL,
        country: user.country,
        city: user.city,
        reason: user.reason,
        profArea: user.profArea,
        availability: user.availability,
        description: user.description,
        salary: user.salary,
      }));
  }, [user]);

  async function onLogout() {
    await auth.signOut();
    navigate("/");
  }

  const onTextChange = (e: any) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.id]: e.target.value,
    }));
  };

  const onCountryChange = (value: string | null, option: any) => {
    if (!option) return;
    setFormData((prevState) => ({
      ...prevState,
      country: option.value,
    }));
  };

  const onProfAreaChange = (value: string | null, option: any) => {
    if (!option) return;
    setFormData((prevState) => ({
      ...prevState,
      profArea: option.value,
    }));
  };

  const onReasonChange = (value: string | null, option: any) => {
    if (!option) return;
    setFormData((prevState) => ({
      ...prevState,
      reason: option.value,
    }));
  };

  const onAvailabilityChange = (value: string | null, option: any) => {
    if (!option) return;
    setFormData((prevState) => ({
      ...prevState,
      availability: option.value,
    }));
  };

  const onSalaryChange = (value: number | string) => {
    setFormData((prevState) => ({
      ...prevState,
      salary: value,
    }));
  };

  const onUploadImage = () => {
    const fileInput = document.getElementById("imageInput");
    if (fileInput) fileInput.click();
  };

  const saveImage = (event: any) => {
    const image = event.target.files[0];

    setFormData((prevState) => ({
      ...prevState,
      photoURL: URL.createObjectURL(image),
    }));

    // we need to upload images to the database immediately

    if (auth.currentUser) {
      storeImage(image, auth.currentUser);
    }
  };

  async function onSubmit() {
    try {
      if (auth.currentUser) {
        await updateCurrentUser(
          name,
          country,
          city,
          reason,
          profArea,
          availability,
          description,
          salary,
          auth.currentUser
        );
      }
      toast.success("Profile details updated");
    } catch (error) {
      toast.error("Could not update the profile details");
    }
  }

  const onCloseButtonClick = () => {
    navigate("/");
  };

  return (
    <>
      <section className="profile-container">
        <h1 className="profile-heading">
          {intl.formatMessage({ id: "header.profile" })}
        </h1>
        <div className="profile-form-container">
          <input
            type="file"
            id="imageInput"
            hidden={true}
            onChange={saveImage}
            accept="image/jpeg, image/png"
            multiple
          />
          <form>
            <div className="profile-form-avatar">
              <UserAvatar name={name} photoURL={photoURL} size={200} />
              <div className="profile-avatar-buttons">
                <Button variant="subtle" color="gray" onClick={onUploadImage}>
                  {intl.formatMessage({ id: "button.uploadImage" })}
                </Button>
                <Button variant="subtle" color="gray" onClick={onLogout}>
                  {intl.formatMessage({ id: "button.signOut" })}
                </Button>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "50px",
              }}
            >
              <TextInput
                id="name"
                label={intl.formatMessage({ id: "profile.fullName" })}
                placeholder={intl.formatMessage({ id: "profile.fullName" })}
                inputWrapperOrder={["label", "input", "description"]}
                mt="sm"
                value={name}
                onChange={onTextChange}
                style={{ flex: 1 }}
              />
              <TextInput
                id="email"
                mt="sm"
                label={intl.formatMessage({ id: "profile.email" })}
                placeholder={intl.formatMessage({ id: "profile.email" })}
                // error="both below the input"
                inputWrapperOrder={["label", "input", "description", "error"]}
                value={email}
                disabled
                style={{ flex: 1 }}
              />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "50px",
              }}
            >
              <Select
                id="country-select"
                mt="sm"
                label={intl.formatMessage({ id: "profile.country" })}
                data={countries.map((area) => ({
                  value: area.code,
                  label: area.name,
                }))}
                checkIconPosition="right"
                value={country}
                onChange={onCountryChange}
                searchable
                placeholder={intl.formatMessage({ id: "profile.country" })}
                style={{ flex: 1 }}
              />
              <TextInput
                id="city"
                mt="sm"
                label={intl.formatMessage({ id: "profile.city" })}
                placeholder={intl.formatMessage({ id: "profile.city" })}
                inputWrapperOrder={["label", "input"]}
                value={city}
                onChange={onTextChange}
                style={{ flex: 1 }}
              />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "50px",
              }}
            >
              <Select
                id="reason"
                mt="sm"
                label={intl.formatMessage({ id: "profile.reason" })}
                data={lists.reasonOptions.map((option) => ({
                  value: option.value,
                  label: intl.formatMessage({
                    id: `lookup.reason.${option.value}`,
                  }),
                }))}
                checkIconPosition="right"
                value={reason}
                onChange={onReasonChange}
                placeholder={intl.formatMessage({ id: "profile.reason" })}
                style={{ flex: 1 }}
              />
              <Select
                id="prof-area"
                mt="sm"
                label={intl.formatMessage({ id: "profile.profArea" })}
                data={lists.professionalAreasOptions.map((option) => ({
                  value: option.value,
                  label: intl.formatMessage({
                    id: `lookup.profArea.${option.value}`,
                  }),
                }))}
                checkIconPosition="right"
                value={profArea}
                onChange={onProfAreaChange}
                searchable
                placeholder={intl.formatMessage({ id: "profile.profArea" })}
                style={{ flex: 1 }}
              />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "50px",
              }}
            >
              <Select
                id="availability"
                mt="sm"
                label={
                  <Group gap={6}>
                    {intl.formatMessage({ id: "profile.availability" })}
                    <Tooltip
                      label={intl.formatMessage({
                        id: "profile.availabilityTooltip",
                      })}
                      withArrow
                      opened={availTooltipOpened}
                      position="top"
                    >
                      <ActionIcon
                        size="xs"
                        color="blue"
                        variant="subtle"
                        onClick={() => setAvailTooltipOpened((o) => !o)}
                        onMouseLeave={() => setAvailTooltipOpened(false)}
                      >
                        <IconInfoCircle size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                }
                data={[
                  { value: "0", label: "0" },
                  { value: "1-5", label: "1-5" },
                  { value: "5-10", label: "5-10" },
                  { value: "10-20", label: "10-20" },
                  { value: "20+", label: "20+" },
                ]}
                checkIconPosition="right"
                value={availability}
                onChange={onAvailabilityChange}
                placeholder={intl.formatMessage({ id: "profile.availability" })}
                style={{ flex: 1 }}
              />
              <NumberInput
                id="salary"
                mt="sm"
                label={intl.formatMessage({ id: "profile.salary" })}
                value={salary}
                onChange={onSalaryChange}
                placeholder={intl.formatMessage({
                  id: "profile.salary",
                })}
                style={{ flex: 1 }}
                min={0}
                max={100}
              />
              {/* <Select
                  id="currency"
                  mt="sm"
                  label={intl.formatMessage({ id: "profile.currency" })}
                  data={[
                    { value: "EUR", label: "EUR" },
                    { value: "USD", label: "USD" },
                    { value: "CAD", label: "CAD" },
                  ]}
                  checkIconPosition="right"
                  value={currency}
                  onChange={onAvailabilityChange}
                  placeholder={intl.formatMessage({
                    id: "profile.currency",
                  })}
                  style={{ width: "110px" }}
                />*/}
            </div>
            <Textarea
              id="description"
              mt="sm"
              label={intl.formatMessage({ id: "profile.description" })}
              placeholder={intl.formatMessage({ id: "profile.description" })}
              value={description}
              onChange={onTextChange}
              maxLength={2000}
              autosize
            />
            <Group justify="end" style={{ marginTop: "16px" }}>
              <Button className="myprofile-save-button" onClick={onSubmit}>
                {intl.formatMessage({ id: "button.save" })}
              </Button>
              <Button variant="default" onClick={onCloseButtonClick}>
                {intl.formatMessage({ id: "button.close" })}
              </Button>
            </Group>
          </form>
        </div>
      </section>
    </>
  );
}
